import { useCallback, useMemo } from "react";
import {
  EditMeUserPreferenceMutation,
  EditMeUserPreferenceMutationVariables,
  MessageThreadSortType,
  TaskStatusType,
  UserPreference,
} from "gql/graphql";
import { useAuth } from "modules/providers/auth-provider";
import { useMutation } from "@apollo/client";
import EDIT_ME_USER_PREFERENCE_MUTATION from "./_networking/edit-me-user-preference";
import { useUsers } from "modules/providers/users-provider";
import { UserPreferenceName } from "modules/utils/Constants";

export type EnterKeySettingValue = "new-line" | "send-message";

export enum DATE_FILTER_VALUE {
  TODAY = "today",
  THIS_WEEK = "this-week",
}

interface DefaultValue {
  booleanValue?: boolean;
  stringValue?: string;
  stringArrayValue?: string[];
}

export type LocaleValue =
  | "sq"
  | "zh-CN"
  | "zh-TW"
  | "en"
  | "tl"
  | "fr"
  | "de"
  | "it"
  | "ja"
  | "pt"
  | "ro"
  | "ru"
  | "es"
  | "vi";

const USER_PREFERENCE_DEFAULT_VALUES: {
  [name in UserPreferenceName]: DefaultValue | undefined;
} = {
  ENTER_KEY_SETTING: { stringValue: "send-message" },
  SMS: { booleanValue: false },
  WEB_IS_HOME_GROUP: { booleanValue: false },
  WEB_IS_HOME_MESSAGE_THREADS_EXPANDED: { booleanValue: true },
  WEB_IS_HOME_DIRECT_MESSAGE_THREADS_EXPANDED: { booleanValue: true },
  WEB_IS_HOME_PINNED_MESSAGE_THREADS_EXPANDED: { booleanValue: true },
  WEB_IS_HOME_TASKS_EXPANDED: { booleanValue: true },
  WEB_IS_PINNED_DIRECT_MESSAGE_THREADS_EXPANDED: { booleanValue: true },
  WEB_IS_PINNED_TASKS_EXPANDED: { booleanValue: true },
  WEB_IS_PINNED_MESSAGE_THREADS_EXPANDED: { booleanValue: true },
  LOCALE: undefined,
  WEB_IS_TASKS_UNREAD_FILTER_TOGGLED: undefined,
  WEB_IS_HOME_UNREAD_FILTER_TOGGLED: undefined,
  WEB_IS_REMINDERS_UNREAD_FILTER_TOGGLED: undefined,
  WEB_HOME_DATE_FILTER: undefined,
  WEB_REMINDERS_DATE_FILTER: undefined,
  WEB_IS_CHATS_UNREAD_FILTER_TOGGLED: undefined,
  WEB_CHATS_DATE_FILTER: undefined,
  WEB_IS_THREADS_UNREAD_FILTER_TOGGLED: undefined,
  WEB_THREADS_DATE_FILTER: undefined,
  DISPLAY_INVITE_BANNER: { booleanValue: true },
  WEB_TASK_ASSIGNED_USER_FILTER: { stringArrayValue: [] },
  WEB_TASK_CREATED_USER_FILTER: { stringArrayValue: [] },
  WEB_TASK_STATUS_FILTER: {
    stringArrayValue: [
      TaskStatusType.InProgress,
      TaskStatusType.NotStarted,
      TaskStatusType.OnHold,
    ],
  },
  WEB_THREAD_CREATED_USER_FILTER: { stringArrayValue: [] },
  WEB_SORT_TYPE: { stringValue: MessageThreadSortType.SortTime },
};

interface IProps {
  userId?: string;
  name?: UserPreferenceName;
}

const useUserPreference = ({ userId, name }: IProps) => {
  const { me } = useAuth();
  const { usersById } = useUsers();
  const user = useMemo(
    () => (userId ? usersById[userId] : undefined),
    [userId, usersById],
  );

  const [editMeUserPreferenceMutation] = useMutation<
    EditMeUserPreferenceMutation,
    EditMeUserPreferenceMutationVariables
  >(EDIT_ME_USER_PREFERENCE_MUTATION);

  const userPreference = useMemo(
    () =>
      user?.userPreferenceList.find((preference) => preference.name === name),
    [user?.userPreferenceList, name],
  );

  const stringValue = useMemo(() => {
    if (!name) return undefined;
    if (!userPreference) {
      return USER_PREFERENCE_DEFAULT_VALUES[name]?.stringValue;
    }
    return userPreference.value;
  }, [userPreference, name]);

  const stringArrayValue = useMemo(() => {
    if (!name) return undefined;
    if (!userPreference) {
      return USER_PREFERENCE_DEFAULT_VALUES[name]?.stringArrayValue;
    }
    const raw = userPreference.value;
    if (!raw || typeof raw !== "string") {
      return USER_PREFERENCE_DEFAULT_VALUES[name]?.stringArrayValue ?? [];
    }
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((v) => typeof v === "string");
      }
      return USER_PREFERENCE_DEFAULT_VALUES[name]?.stringArrayValue ?? [];
    } catch (err) {
      return USER_PREFERENCE_DEFAULT_VALUES[name]?.stringArrayValue ?? [];
    }
  }, [userPreference, name]);

  const booleanValue = useMemo(() => {
    if (!name) return undefined;
    if (!userPreference) {
      return USER_PREFERENCE_DEFAULT_VALUES[name]?.booleanValue;
    }
    return userPreference?.value === "true";
  }, [userPreference, name]);

  const upsertUserPreference = useCallback(
    async (value: string | string[] | boolean | null) => {
      if (!me || !user || !name) return;

      if (me.id !== user.id) {
        throw new Error("Only the current user can edit their own preferences");
      }

      let normalizedValue: string | null;
      if (typeof value === "boolean") {
        normalizedValue = value ? "true" : "false";
      } else if (Array.isArray(value)) {
        normalizedValue = JSON.stringify(value);
      } else {
        normalizedValue = value;
      }

      const optimisticUserPreference: UserPreference | undefined =
        normalizedValue !== null
          ? {
              __typename: "UserPreference",
              id: me.id,
              userId: me.id,
              name,
              value: normalizedValue,
            }
          : undefined;

      const optimisticUserPreferenceList = [
        ...me.userPreferenceList.filter((pref) => pref.name !== name),
        ...(optimisticUserPreference ? [optimisticUserPreference] : []),
      ];

      await editMeUserPreferenceMutation({
        variables: {
          name,
          value: normalizedValue,
        },
        optimisticResponse: {
          editMe: {
            __typename: "User",
            id: me.id,
            userPreferenceList: optimisticUserPreferenceList,
          },
        },
      });
    },
    [editMeUserPreferenceMutation, me, name, user],
  );

  return {
    stringValue,
    stringArrayValue,
    booleanValue,
    upsertUserPreference,
  };
};

export default useUserPreference;
